'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PercentCalculator() {
  const t = useTranslations("tools.percentCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Basic calculator state
  const [basicPercent, setBasicPercent] = useState('');
  const [basicNumber, setBasicNumber] = useState('');
  const [basicResult, setBasicResult] = useState('');

  // Question 1 state
  const [q1Percent, setQ1Percent] = useState('');
  const [q1Number, setQ1Number] = useState('');
  const [q1Answer, setQ1Answer] = useState('');
  const [q1Visible, setQ1Visible] = useState(false);

  // Question 2 state
  const [q2Part, setQ2Part] = useState('');
  const [q2Whole, setQ2Whole] = useState('');
  const [q2Answer, setQ2Answer] = useState('');
  const [q2Visible, setQ2Visible] = useState(false);

  // Question 3 state
  const [q3Part, setQ3Part] = useState('');
  const [q3Percent, setQ3Percent] = useState('');
  const [q3Answer, setQ3Answer] = useState('');
  const [q3Visible, setQ3Visible] = useState(false);

  // Change calculator state
  const [changeOriginal, setChangeOriginal] = useState('');
  const [changeType, setChangeType] = useState('increase');
  const [changePercent, setChangePercent] = useState('');
  const [changeResult, setChangeResult] = useState('');
  const [changeNewValue, setChangeNewValue] = useState('');
  const [changeAbsolute, setChangeAbsolute] = useState('');
  const [changeVisible, setChangeVisible] = useState(false);

  // Difference calculator state
  const [diffValue1, setDiffValue1] = useState('');
  const [diffValue2, setDiffValue2] = useState('');
  const [diffPercentage, setDiffPercentage] = useState('');
  const [diffAbsolute, setDiffAbsolute] = useState('');
  const [diffVisible, setDiffVisible] = useState(false);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const roundToTwo = (num: number) => {
    return Math.round(num * 100) / 100;
  };

  const handleBasicCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const percent = parseFloat(basicPercent);
    const number = parseFloat(basicNumber);
    const result = parseFloat(basicResult);

    if (!isNaN(percent) && !isNaN(number)) {
      const calculated = (percent / 100) * number;
      setBasicResult(roundToTwo(calculated).toString());
    } else if (!isNaN(percent) && !isNaN(result)) {
      const calculated = (result / percent) * 100;
      setBasicNumber(roundToTwo(calculated).toString());
    } else if (!isNaN(number) && !isNaN(result)) {
      const calculated = (result / number) * 100;
      setBasicPercent(roundToTwo(calculated).toString());
    }
  };

  const handleBasicClear = () => {
    setBasicPercent('');
    setBasicNumber('');
    setBasicResult('');
  };

  const handleQ1Calculate = (e: React.FormEvent) => {
    e.preventDefault();
    const percent = parseFloat(q1Percent);
    const number = parseFloat(q1Number);

    if (!isNaN(percent) && !isNaN(number)) {
      const result = (percent / 100) * number;
      setQ1Answer(roundToTwo(result).toString());
      setQ1Visible(true);
    }
  };

  const handleQ2Calculate = (e: React.FormEvent) => {
    e.preventDefault();
    const part = parseFloat(q2Part);
    const whole = parseFloat(q2Whole);

    if (!isNaN(part) && !isNaN(whole) && whole !== 0) {
      const result = (part / whole) * 100;
      setQ2Answer(roundToTwo(result) + '%');
      setQ2Visible(true);
    }
  };

  const handleQ3Calculate = (e: React.FormEvent) => {
    e.preventDefault();
    const part = parseFloat(q3Part);
    const percent = parseFloat(q3Percent);

    if (!isNaN(part) && !isNaN(percent) && percent !== 0) {
      const result = (part / percent) * 100;
      setQ3Answer(roundToTwo(result).toString());
      setQ3Visible(true);
    }
  };

  const handleChangeCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const original = parseFloat(changeOriginal);
    const percent = parseFloat(changePercent);
    const result = parseFloat(changeResult);

    if (!isNaN(original) && !isNaN(percent)) {
      const multiplier = changeType === 'increase' ? (1 + percent / 100) : (1 - percent / 100);
      const newValue = original * multiplier;
      const absoluteChange = Math.abs(newValue - original);

      setChangeResult(roundToTwo(newValue).toString());
      setChangeNewValue(roundToTwo(newValue).toString());
      setChangeAbsolute(roundToTwo(absoluteChange).toString());
      setChangeVisible(true);
    } else if (!isNaN(original) && !isNaN(result)) {
      const change = result - original;
      const percentChange = (Math.abs(change) / original) * 100;

      setChangePercent(roundToTwo(percentChange).toString());
      setChangeNewValue(roundToTwo(result).toString());
      setChangeAbsolute(roundToTwo(Math.abs(change)).toString());
      setChangeVisible(true);
    }
  };

  const handleChangeClear = () => {
    setChangeOriginal('');
    setChangePercent('');
    setChangeResult('');
    setChangeVisible(false);
  };

  const handleDiffCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const value1 = parseFloat(diffValue1);
    const value2 = parseFloat(diffValue2);

    if (!isNaN(value1) && !isNaN(value2)) {
      const absoluteDiff = Math.abs(value1 - value2);
      const average = (value1 + value2) / 2;
      const percentDiff = average !== 0 ? (absoluteDiff / average) * 100 : 0;

      setDiffPercentage(roundToTwo(percentDiff) + '%');
      setDiffAbsolute(roundToTwo(absoluteDiff).toString());
      setDiffVisible(true);
    }
  };

  const handleDiffClear = () => {
    setDiffValue1('');
    setDiffValue2('');
    setDiffVisible(false);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '800px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const toolSubtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, margin: 0 },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const cardH3Style = getResponsiveStyle(
    {
      color: colors.textPrimary,
      margin: '0 0 1.5rem 0',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const formStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.25rem'
  };

  const formRowStyle = getResponsiveStyle(
    {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap' as const,
      color: colors.textSecondary
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white',
      minWidth: '120px'
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.8rem 1.1rem', fontSize: '1rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' }
  );

  const numberInputStyle = {
    ...inputStyle,
    maxWidth: '150px'
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    paddingRight: '2.5rem',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23334155'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.5rem center',
    backgroundSize: '1.5rem',
    appearance: 'none' as const
  };

  const readonlyInputStyle = {
    ...inputStyle,
    background: '#f8fafc'
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
      alignSelf: 'flex-start' as const
    },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      background: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    { padding: '0.875rem 2rem', fontSize: '1rem', marginLeft: '0', marginTop: '0.5rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', marginLeft: '0.75rem', marginTop: '0', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', marginLeft: '0.75rem', marginTop: '0', width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      marginTop: '2rem',
      border: '2px solid #86efac'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    {
      color: '#166534',
      margin: '0 0 1.5rem 0',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1rem', flexDirection: 'column' as const, alignItems: 'flex-start' as const, gap: '0.5rem' },
    { padding: '1.125rem', flexDirection: 'row' as const },
    { padding: '1.25rem', flexDirection: 'row' as const }
  );

  const resultLabelStyle = getResponsiveStyle(
    { color: '#475569', fontWeight: 500 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: '#166534', fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const inputGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem'
  };

  const inputGroupLabelStyle = getResponsiveStyle(
    { color: '#475569', fontWeight: 500 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>🧮 {t("toolTitle")}</h2>
            <p style={toolSubtitleStyle}>{t("toolSubtitle")}</p>
          </div>

          <div style={calculatorCardStyle}>
            <h3 style={cardH3Style}><span style={{ fontSize: '1.5rem' }}>📊</span> {t("basicCalculatorTitle")}</h3>
            <form style={formStyle} onSubmit={handleBasicCalculate}>
              <div style={formRowStyle}>
                <input
                  type="number"
                  step="any"
                  placeholder="25"
                  value={basicPercent}
                  onChange={(e) => { setBasicPercent(e.target.value); setBasicResult(''); }}
                  style={numberInputStyle}
                />
                <span>% {t("of")}</span>
                <input
                  type="number"
                  step="any"
                  placeholder="200"
                  value={basicNumber}
                  onChange={(e) => { setBasicNumber(e.target.value); setBasicResult(''); }}
                  style={numberInputStyle}
                />
                <span>=</span>
                <input
                  type="number"
                  step="any"
                  placeholder={t("result")}
                  value={basicResult}
                  readOnly
                  style={readonlyInputStyle}
                />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: screenSize === 'mobile' ? '0.5rem' : '0.75rem' }}>
                <button type="submit" style={calculateBtnStyle}>{t("calculate")}</button>
                <button type="button" style={clearBtnStyle} onClick={handleBasicClear}>{t("clear")}</button>
              </div>
            </form>
          </div>

          <div style={calculatorCardStyle}>
            <h3 style={cardH3Style}><span style={{ fontSize: '1.5rem' }}>💬</span> {t("commonQuestionsTitle")}</h3>

            <form style={{ ...formStyle, marginBottom: '2rem' }} onSubmit={handleQ1Calculate}>
              <div style={formRowStyle}>
                <span>{t("whatIs")}</span>
                <input
                  type="number"
                  step="any"
                  placeholder="15"
                  value={q1Percent}
                  onChange={(e) => setQ1Percent(e.target.value)}
                  required
                  style={numberInputStyle}
                />
                <span>% {t("of")}</span>
                <input
                  type="number"
                  step="any"
                  placeholder="300"
                  value={q1Number}
                  onChange={(e) => setQ1Number(e.target.value)}
                  required
                  style={numberInputStyle}
                />
                <span>?</span>
              </div>
              <button type="submit" style={calculateBtnStyle}>{t("calculate")}</button>
              {q1Visible && (
                <div style={{ ...resultsStyle, marginTop: '1rem' }}>
                  <div style={{ ...resultItemStyle, marginBottom: 0 }}>
                    <span style={resultLabelStyle}>{t("answer")}:</span>
                    <span style={resultValueStyle}>{q1Answer}</span>
                  </div>
                </div>
              )}
            </form>

            <form style={{ ...formStyle, marginBottom: '2rem' }} onSubmit={handleQ2Calculate}>
              <div style={formRowStyle}>
                <input
                  type="number"
                  step="any"
                  placeholder="50"
                  value={q2Part}
                  onChange={(e) => setQ2Part(e.target.value)}
                  required
                  style={numberInputStyle}
                />
                <span>{t("isWhatPercentOf")}</span>
                <input
                  type="number"
                  step="any"
                  placeholder="200"
                  value={q2Whole}
                  onChange={(e) => setQ2Whole(e.target.value)}
                  required
                  style={numberInputStyle}
                />
                <span>?</span>
              </div>
              <button type="submit" style={calculateBtnStyle}>{t("calculate")}</button>
              {q2Visible && (
                <div style={{ ...resultsStyle, marginTop: '1rem' }}>
                  <div style={{ ...resultItemStyle, marginBottom: 0 }}>
                    <span style={resultLabelStyle}>{t("answer")}:</span>
                    <span style={resultValueStyle}>{q2Answer}</span>
                  </div>
                </div>
              )}
            </form>

            <form style={formStyle} onSubmit={handleQ3Calculate}>
              <div style={formRowStyle}>
                <input
                  type="number"
                  step="any"
                  placeholder="75"
                  value={q3Part}
                  onChange={(e) => setQ3Part(e.target.value)}
                  required
                  style={numberInputStyle}
                />
                <span>{t("is")}</span>
                <input
                  type="number"
                  step="any"
                  placeholder="25"
                  value={q3Percent}
                  onChange={(e) => setQ3Percent(e.target.value)}
                  required
                  style={numberInputStyle}
                />
                <span>% {t("ofWhat")}?</span>
              </div>
              <button type="submit" style={calculateBtnStyle}>{t("calculate")}</button>
              {q3Visible && (
                <div style={{ ...resultsStyle, marginTop: '1rem' }}>
                  <div style={{ ...resultItemStyle, marginBottom: 0 }}>
                    <span style={resultLabelStyle}>{t("answer")}:</span>
                    <span style={resultValueStyle}>{q3Answer}</span>
                  </div>
                </div>
              )}
            </form>
          </div>

          <div style={calculatorCardStyle}>
            <h3 style={cardH3Style}><span style={{ fontSize: '1.5rem' }}>📈</span> {t("changeCalculatorTitle")}</h3>
            <form style={formStyle} onSubmit={handleChangeCalculate}>
              <div style={inputGroupStyle}>
                <label htmlFor="change-original" style={inputGroupLabelStyle}>{t("originalValue")}</label>
                <input
                  type="number"
                  id="change-original"
                  step="any"
                  placeholder="100"
                  value={changeOriginal}
                  onChange={(e) => { setChangeOriginal(e.target.value); setChangeResult(''); setChangeVisible(false); }}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={formRowStyle}>
                <select
                  id="change-type"
                  value={changeType}
                  onChange={(e) => { setChangeType(e.target.value); setChangeResult(''); setChangeVisible(false); }}
                  style={selectStyle}
                >
                  <option value="increase">{t("increase")}</option>
                  <option value="decrease">{t("decrease")}</option>
                </select>
                <span>{t("by")}</span>
                <input
                  type="number"
                  step="any"
                  placeholder="20"
                  value={changePercent}
                  onChange={(e) => { setChangePercent(e.target.value); setChangeResult(''); setChangeVisible(false); }}
                  style={numberInputStyle}
                />
                <span>% =</span>
                <input
                  type="number"
                  step="any"
                  placeholder={t("result")}
                  value={changeResult}
                  readOnly
                  style={readonlyInputStyle}
                />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: screenSize === 'mobile' ? '0.5rem' : '0.75rem' }}>
                <button type="submit" style={calculateBtnStyle}>{t("calculate")}</button>
                <button type="button" style={clearBtnStyle} onClick={handleChangeClear}>{t("clear")}</button>
              </div>
              {changeVisible && (
                <div style={resultsStyle}>
                  <h3 style={resultsH3Style}>✨ {t("results")}</h3>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("newValue")}:</span>
                    <span style={resultValueStyle}>{changeNewValue}</span>
                  </div>
                  <div style={{ ...resultItemStyle, marginBottom: 0 }}>
                    <span style={resultLabelStyle}>{t("absoluteChange")}:</span>
                    <span style={resultValueStyle}>{changeAbsolute}</span>
                  </div>
                </div>
              )}
            </form>
          </div>

          <div style={calculatorCardStyle}>
            <h3 style={cardH3Style}><span style={{ fontSize: '1.5rem' }}>🔄</span> {t("differenceCalculatorTitle")}</h3>
            <form style={formStyle} onSubmit={handleDiffCalculate}>
              <div style={inputGroupStyle}>
                <label htmlFor="diff-value1" style={inputGroupLabelStyle}>{t("value1")}</label>
                <input
                  type="number"
                  id="diff-value1"
                  step="any"
                  placeholder="100"
                  value={diffValue1}
                  onChange={(e) => setDiffValue1(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={inputGroupStyle}>
                <label htmlFor="diff-value2" style={inputGroupLabelStyle}>{t("value2")}</label>
                <input
                  type="number"
                  id="diff-value2"
                  step="any"
                  placeholder="150"
                  value={diffValue2}
                  onChange={(e) => setDiffValue2(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: screenSize === 'mobile' ? '0.5rem' : '0.75rem' }}>
                <button type="submit" style={calculateBtnStyle}>{t("calculate")}</button>
                <button type="button" style={clearBtnStyle} onClick={handleDiffClear}>{t("clear")}</button>
              </div>
              {diffVisible && (
                <div style={resultsStyle}>
                  <h3 style={resultsH3Style}>✨ {t("results")}</h3>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("percentageDifference")}:</span>
                    <span style={resultValueStyle}>{diffPercentage}</span>
                  </div>
                  <div style={{ ...resultItemStyle, marginBottom: 0 }}>
                    <span style={resultLabelStyle}>{t("absoluteDifference")}:</span>
                    <span style={resultValueStyle}>{diffAbsolute}</span>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li>{t("howToUseStep1")}</li>
          <li>{t("howToUseStep2")}</li>
          <li>{t("howToUseStep3")}</li>
          <li>{t("howToUseStep4")}</li>
          <li>{t("howToUseStep5")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>

        <h3 style={h3Style}>{t("insightsFinanceTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsFinanceParagraph")}</p>

        <h3 style={h3Style}>{t("insightsEducationTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsEducationParagraph")}</p>

        <h3 style={h3Style}>{t("insightsBusinessTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsBusinessParagraph")}</p>

        <h3 style={h3Style}>{t("insightsDataTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsDataParagraph")}</p>

        <h3 style={h3Style}>{t("insightsDailyTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsDailyParagraph")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailsBasicTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsBasicParagraph")}</p>

        <h3 style={h3Style}>{t("detailsIncreaseTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsIncreaseParagraph")}</p>

        <h3 style={h3Style}>{t("detailsDecreaseTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsDecreaseParagraph")}</p>

        <h3 style={h3Style}>{t("detailsDifferenceTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsDifferenceParagraph")}</p>

        <h3 style={h3Style}>{t("detailsBestPracticesTitle")}</h3>
        <ul style={listStyle}>
          <li>{t("detailsBestPractice1")}</li>
          <li>{t("detailsBestPractice2")}</li>
          <li>{t("detailsBestPractice3")}</li>
          <li>{t("detailsBestPractice4")}</li>
          <li>{t("detailsBestPractice5")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={h3Style}>{t("faqQuestion1")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer1")}</p>

        <h3 style={h3Style}>{t("faqQuestion2")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer2")}</p>

        <h3 style={h3Style}>{t("faqQuestion3")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer3")}</p>

        <h3 style={h3Style}>{t("faqQuestion4")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer4")}</p>

        <h3 style={h3Style}>{t("faqQuestion5")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer5")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={listStyle}>
          <li>
            <a href="https://brightseotools.com/percentage-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Percentage Calculator - Bright SEO Tools
            </a>
          </li>
          <li>
            <a href="https://plusvibe.ai/free-percentage-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Free Percentage Calculator - PlusVibe
            </a>
          </li>
          <li>
            <a href="https://abcsubmit.com/site/blog/what-is-the-percentage-of-percentage-calculator-online/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Free online Percentage Calculator - AbcSubmit
            </a>
          </li>
          <li>
            <a href="https://sadiqhub.com/online-percentage-calculator-guide/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              7 Benefits of Using an Online Percentage Calculator
            </a>
          </li>
          <li>
            <a href="https://www.barchart.com/story/news/25450754/percentagecalculator24-launches-the-most-comprehensive-percentage-calculator-on-the-web" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              PercentageCalculator24 Launches Comprehensive Calculator
            </a>
          </li>
          <li>
            <a href="https://salmankhanseo.com/percentage-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Percentage Calculator Tool - Salman Khan SEO
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}